<template>
    <b-modal
        title-tag="h2"
        :title="isEditMode ? 'Edit Communication' : 'Log Communication'"
        :lazy="true"
        :visible="isOpen"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-cols="3" label="Patient:" label-for="patient">
            <!-- CLIENT ONLY CAN SELECT FROM ITS RELATIONSHIPS -->
            <vet-select
                v-if="module == COMMUNICATION.MODULE_CLIENT"
                module="client-patient"
                placeholder="Patient Name (optional)"
                label="name"
                :client-id="clientId"
                v-model="patient"
                :showMenu="false"
                name="patient_name"
            />
            <vet-select
                v-else
                module="patient-full-details"
                placeholder="Patient Name (optional)"
                label="name"
                v-model="patient"
                :showMenu="false"
                name="patient_name"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Date:" label-for="date">
            <date-picker
                :config="datePickerOptions"
                v-model="date"
                placeholder="Date (required)"
                v-validate="'required'"
                name="date"
                :class="{
                    'border-danger': formError.includes('date')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Time:" label-for="time">
            <time-picker
                v-model="time"
                placeholder="Time (required)"
                v-validate="'required'"
                name="time"
                format="datetime"
                :class="{
                    'form-control': true,
                    'border-danger': formError.includes('time')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Type:" label-for="type">
            <b-form-select
                v-model="type"
                :options="typeOptions"
                value-field="id"
                text-field="value"
            ></b-form-select>
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Message:"
            label-for="message"
        ></b-form-group>

        <div v-if="type === 4">
            <tinymce
                :init="tinymceConfig"
                placeholder="Communication (required)"
                v-model="message"
            />
        </div>
        <editable
            id="message"
            v-else
            v-model="message"
            placeholder="Communication (required)"
            :defaultHeight="146"
            :class="{
                'border-danger': formError.includes('message')
            }"
        />

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSaving" variant="primary" @click="save">
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import VetSelect from "@/module/commons/vet-select";
import Communication from "@/services/communications";
import { DateTime } from "luxon";
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import TimePicker from "@/module/commons/time-picker";
import COMMUNICATION from "@/constants/communications";
import Editable from "@/components/elements/editable-textarea";

export default {
    components: {
        VetSelect,
        TimePicker,
        Loader,
        tinymce,
        Editable
    },
    props: {
        module: {
            String,
            required: true,
            validator: function (value) {
                // The value must match one of these strings
                return (
                    [
                        COMMUNICATION.MODULE_CLIENT,
                        // COMMUNICATION.MODULE_PATIENT,    // currently patient module still using apps endpoint
                        COMMUNICATION.MODULE_CONTACT
                    ].indexOf(value) !== -1
                );
            }
        },
        clientId: {
            type: String
        },
        contactId: {
            type: String
        }
    },
    data() {
        return {
            patient: null,
            date: null,
            time: null,
            type: 1, // phone
            message: "",
            COMMUNICATION: COMMUNICATION,
            isOpen: false,
            isSaving: false,
            isLoadingList: true,
            patientOptions: [],
            datePickerOptions: {},
            timePickerOptions: {},
            typeOptions: [],
            dateTimeFormat: {},
            isEditMode: false,
            editRecord: {},
            tinymceConfig: {
                ...tinymceHelper.core,
                toolbar: tinymceHelper.toolbar.default,
                plugins: tinymceHelper.plugins,
                statusbar: true
            },
            formError: []
        };
    },
    computed: {},
    watch: {
        date(value) {
            if (value) {
                this.formError = this.formError.filter(err => err !== "date");
            }
        },
        time(value) {
            if (value) {
                this.formError = this.formError.filter(err => err !== "time");
            }
        }
    },
    methods: {
        async open(record = null) {
            this.initDateTimeFormat();
            this.isOpen = true;
            this.typeOptions = await Communication.getCommunicationTypes();
            // edit mode
            if (record) {
                this.isEditMode = true;
                this.editRecord = record;
                this.applyExistingRecord(record);
            }
        },
        close() {
            this.isOpen = false;
        },
        applyExistingRecord(record) {
            this.patient = record.patient;
            this.type = record.type;
            this.message = record.content;
            this.date = this._carbonDateDisplay(record.date);
            this.time = this._fromDateCarbon(record.date)
                .setZone(this._timeZone)
                .toFormat(this.dateTimeFormat.time.luxon);
        },
        initDateTimeFormat() {
            // explicitly store date and time format for easier references.
            // why have two formats (luxon & moment)?
            // luxon = for our processing logic
            // moment = used by datepicker
            const dateTimeFormat = {
                date: {
                    luxon: this._getLuxonFormat(),
                    moment: this._getDateFormat()
                },
                time: {
                    luxon: "hh:mm a",
                    moment: "hh:mm A"
                }
            };

            this.dateTimeFormat = dateTimeFormat;
            this.datePickerOptions = { format: dateTimeFormat.date.moment };
            this.timePickerOptions = { format: dateTimeFormat.time.moment };

            // default value
            const luxonDateTime = DateTime.local().setZone(this._timeZone);
            this.date = luxonDateTime.toFormat(dateTimeFormat.date.luxon);
            this.time = luxonDateTime.toFormat(dateTimeFormat.time.luxon);
        },
        convertDateTimeToISO(date, time) {
            if (DateTime.isDateTime(time)) {
                time = time.toFormat("hh:mm a");
            }

            let dateTimeStr = `${date} ${time}`;
            let format = `${this.dateTimeFormat.date.luxon} ${this.dateTimeFormat.time.luxon}`;

            // from user's timezone to UTC, and format as ISO
            let result = DateTime.fromFormat(dateTimeStr, format, {
                zone: this._timeZone
            }).toISO({
                suppressMilliseconds: true
            });

            return result;
        },
        async save() {
            try {
                let hasError = false;
                this.formError = [];

                if (!this.date || this.date === "") {
                    this.formError.push("date");
                    hasError = true;
                }

                if (!this.time || this.time === "") {
                    this.formError.push("time");
                    hasError = true;
                }

                if (hasError) {
                    return;
                }

                if (this.message === "") {
                    throw new Error(" Message is required");
                }

                const patientId = this.patient ? this.patient.id : "";
                const properDate = this.convertDateTimeToISO(
                    this.date,
                    this.time
                );

                this.isSaving = true;
                let isSaveSuccess = false;
                let params = {
                    content: this.message,
                    patient_id: patientId,
                    business_id: this.$store.state.setting.business.id,
                    date: properDate,
                    type: this.type,
                    is_system_generated: false
                };

                if (this.module == COMMUNICATION.MODULE_CLIENT) {
                    params.client_id = this.clientId;
                } else if (this.module == COMMUNICATION.MODULE_CONTACT) {
                    let isCompany = this.$store.state.contact.isCompany;
                    params.contact_id = this.contactId;
                    if (isCompany) {
                        params.contact_type = 1;
                    }
                }

                // update
                if (this.isEditMode) {
                    isSaveSuccess = await Communication.updateCommunication(
                        this.editRecord.id, // communication id
                        params
                    );
                }
                // add
                else {
                    isSaveSuccess = await Communication.saveCommunication(
                        params
                    );
                }

                if (!isSaveSuccess) {
                    throw new Error("Error: Unable to save");
                }

                this._message(" Communication saved");
                this.close();

                this.$emit("onsuccess");
            } catch (e) {
                this._message(e.message, "danger");
                this.isSaving = false;
            }
        }
    }
};
</script>
